// balken-vergleich — ersetzt Marianns Balken-SVG.
//
// Vier Werte nebeneinander, damit man den Groessenunterschied sieht statt ihn
// aus Zahlen zu erschliessen. Bewusst kein Diagramm-Framework: fuer vier Balken
// waere das ein Vielfaches an Ladegewicht fuer nichts.
//
// Wichtiger als die Balken sind hier die Zahlen. Ein Balkendiagramm, dessen
// Aussage nur in der Laenge steckt, ist fuer alle unbrauchbar, die es nicht
// sehen koennen — deshalb steht neben jedem Balken der Wert als Text, und die
// Balken selbst sind aus dem Vorlesegeraet genommen.
//
// Einstellungen am Wrapper:
//   data-titel           Ueberschrift
//   data-einheit         Standard "€"
//   data-quellenangabe   klein unter der Grafik
//
// Zeilenformat: Bezeichnung | Zahl | Zusatz | hervorheben (4. Feld nicht leer
// = dieser Balken wird in Markenfarbe gefuellt)
import { el, leerHinweis } from '@lib/bausteine/dom';
import { formatiere, zerlegeWert, zerlegeZeilen } from '@lib/bausteine/zerlege';
import { zeichneAus } from '@lib/bausteine/auszeichnung';

export function balkenVergleich(wurzel: HTMLElement, quelle: string): Node {
  const zeilen = zerlegeZeilen(quelle);

  const balken = zeilen.map((zeile) => {
    const [name, roh, zusatz, hervorheben] = zeile.felder;
    const wert = zerlegeWert(roh ?? '');
    return {
      name: name ?? '',
      zahl: wert.zahl,
      anzeige: wert.zahl === null ? wert.roh : formatiere(wert.zahl, wert.dezimalstellen),
      zusatz: zusatz ?? '',
      hervorgehoben: Boolean(hervorheben && hervorheben.trim() !== ''),
    };
  });

  if (balken.length === 0) {
    return leerHinweis('Noch keine Werte. Format: Bezeichnung | Zahl | Zusatz');
  }

  const titel = (wurzel.getAttribute('data-titel') ?? '').trim();
  const einheit = wurzel.getAttribute('data-einheit') ?? '€';
  const quellenangabe = (wurzel.getAttribute('data-quellenangabe') ?? '').trim();

  // Negative Werte kommen in diesen Ratgebern nicht vor; der Bezugspunkt ist
  // immer null, damit Laengenverhaeltnisse ehrlich bleiben.
  const groesster = Math.max(...balken.map((eintrag) => eintrag.zahl ?? 0), 0);

  return el(
    'figure',
    { class: 'eoe-reset eoe-balken' },
    titel && el('figcaption', { class: 'eoe-titel' }, titel),
    el(
      'ul',
      { class: 'eoe-balken__liste' },
      ...balken.map((eintrag) => {
        const anteil =
          groesster > 0 && eintrag.zahl !== null ? (eintrag.zahl / groesster) * 100 : 0;

        return el(
          'li',
          { class: `eoe-balken__zeile${eintrag.hervorgehoben ? ' eoe-balken__zeile--hervor' : ''}` },
          el('p', { class: 'eoe-balken__name' }, zeichneAus(eintrag.name)),
          el(
            'div',
            { class: 'eoe-balken__spur' },
            el('span', {
              class: 'eoe-balken__fuellung',
              style: `inline-size: ${anteil}%`,
              'aria-hidden': 'true',
            }),
            el(
              'span',
              { class: 'eoe-balken__wert' },
              eintrag.anzeige + (eintrag.zahl !== null && einheit ? ` ${einheit}` : ''),
            ),
          ),
          eintrag.zusatz && el('p', { class: 'eoe-balken__zusatz' }, zeichneAus(eintrag.zusatz)),
        );
      }),
    ),
    quellenangabe && el('p', { class: 'eoe-fussnote' }, quellenangabe),
  );
}
